package com.tovtalk.ui.components

import androidx.compose.animation.core.animateDpAsState
import androidx.compose.animation.core.tween
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.hoverable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.interaction.collectIsHoveredAsState
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.draw.shadow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.zIndex
import com.tovtalk.ui.theme.TovTalkTheme
import com.tovtalk.ui.theme.TovTalkDurations
import com.tovtalk.ui.theme.TovTalkEasings
import com.tovtalk.ui.adaptive.hasDrawer

private val RAIL_WIDTH = 72.dp
private val EXPANDED_WIDTH = 220.dp

@Composable
fun TovTalkNavigationRail(
    items: List<NavigationItem>,
    selectedRoute: String,
    onItemClick: (NavigationItem) -> Unit,
    modifier: Modifier = Modifier,
    bottomItems: List<NavigationItem> = emptyList(),
) {
    val isDrawerMode = hasDrawer()
    val collapsedWidth = if (isDrawerMode) EXPANDED_WIDTH else RAIL_WIDTH

    val interactionSource = remember { MutableInteractionSource() }
    val isHovered by interactionSource.collectIsHoveredAsState()
    var isTapExpanded by remember { mutableStateOf(false) }
    // In drawer mode the rail is always expanded (permanent drawer).
    // In rail mode use the existing hover/tap logic.
    val isExpanded = if (isDrawerMode) {
        true
    } else {
        isHovered || isTapExpanded
    }

    val expandedWidth by animateDpAsState(
        targetValue = if (isExpanded) EXPANDED_WIDTH else collapsedWidth,
        animationSpec = tween(
            durationMillis = TovTalkDurations.normal,
            easing = TovTalkEasings.standard
        )
    )

    // In drawer mode: fixed 220dp, no scrim needed.
    // In rail mode: fixed 72dp in layout flow, overlay expands on top.
    // When tap-expanded (rail mode only), the outer Box grows to fillMaxSize so the scrim can cover the full screen.
    val outerModifier = if (!isDrawerMode && isTapExpanded) {
        modifier.fillMaxSize()
    } else {
        modifier.width(collapsedWidth).fillMaxHeight()
    }
    Box(modifier = outerModifier) {
        // Transparent full-screen scrim behind the rail overlay (rail mode only).
        // Covers the content area so tapping outside collapses the rail.
        if (!isDrawerMode && isTapExpanded) {
            Box(
                modifier = Modifier
                    .fillMaxSize()
                    .zIndex(9f)
                    .clickable(
                        indication = null,
                        interactionSource = remember { MutableInteractionSource() }
                    ) { isTapExpanded = false }
            )
        }
        Box(
            modifier = Modifier
                .fillMaxHeight()
                .width(expandedWidth)
                .shadow(if (isExpanded) TovTalkTheme.elevation.level3 else 0.dp)
                .background(TovTalkTheme.colors.backgroundSecondary)
                .hoverable(interactionSource)
                .clickable(
                    indication = null,
                    interactionSource = remember { MutableInteractionSource() }
                ) { isTapExpanded = !isTapExpanded }
                .zIndex(10f)
        ) {
            Column(
                modifier = Modifier.fillMaxSize().padding(vertical = TovTalkTheme.spacing.sm),
                horizontalAlignment = Alignment.CenterHorizontally
            ) {
                Text(
                    text = "TovTalk",
                    style = TovTalkTheme.typography.titleSmall,
                    color = TovTalkTheme.colors.textPrimary,
                    modifier = Modifier.padding(bottom = TovTalkTheme.spacing.lg)
                )

                items.forEach { item ->
                    RailItem(
                        item = item,
                        isSelected = item.route == selectedRoute,
                        isExpanded = isExpanded,
                        onClick = {
                            isTapExpanded = false
                            onItemClick(item)
                        }
                    )
                }

                Spacer(modifier = Modifier.weight(1f))

                bottomItems.forEach { item ->
                    RailItem(
                        item = item,
                        isSelected = item.route == selectedRoute,
                        isExpanded = isExpanded,
                        onClick = {
                            isTapExpanded = false
                            onItemClick(item)
                        }
                    )
                }
            }
        }
    }
}

@Composable
private fun RailItem(
    item: NavigationItem,
    isSelected: Boolean,
    isExpanded: Boolean,
    onClick: () -> Unit
) {
    val backgroundColor = if (isSelected) {
        TovTalkTheme.colors.brand.copy(alpha = TovTalkTheme.opacity.subtle)
    } else {
        androidx.compose.ui.graphics.Color.Transparent
    }
    val contentColor = if (isSelected) {
        TovTalkTheme.colors.brand
    } else {
        TovTalkTheme.colors.textSecondary
    }

    Row(
        modifier = Modifier
            .fillMaxWidth()
            .padding(horizontal = TovTalkTheme.spacing.xs, vertical = TovTalkTheme.spacing.xxs)
            .clip(RoundedCornerShape(TovTalkTheme.radius.md))
            .background(backgroundColor)
            .clickable(onClick = onClick)
            .padding(TovTalkTheme.spacing.sm),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.Start
    ) {
        BadgedBox(
            badge = {
                if (item.badgeCount > 0) {
                    Badge { Text(item.badgeCount.toString()) }
                } else if (item.showBadge) {
                    Badge()
                }
            }
        ) {
            Icon(
                imageVector = if (isSelected) item.selectedIcon else item.icon,
                contentDescription = item.label,
                tint = contentColor,
                modifier = Modifier.size(TovTalkTheme.sizes.iconMd)
            )
        }

        if (isExpanded) {
            Spacer(modifier = Modifier.width(TovTalkTheme.spacing.sm))
            Text(
                text = item.label,
                style = TovTalkTheme.typography.bodyMedium,
                color = contentColor,
                maxLines = 1,
            )
        }
    }
}
